import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, Copy, Flag, Loader2, RotateCcw, Sparkles } from 'lucide-react';
import { Message, DiligenceDocumentRun } from '../types';
import ChatAudioAttachment from './ChatAudioAttachment';
import ChatMentionText from './ChatMentionText';
import { refreshChatMentionTitles } from './chatMentionEditing';
import type { MentionableChat } from '../shared/chatMentions';
import ChatImageGallery from './ChatImageGallery';
import ChatDocumentAttachment from './ChatDocumentAttachment';
import DiligenceArtifactList from './DiligenceArtifactList';
import { getMessageArtifacts } from '../utils/documentUiState';
import { getActivityStartTime } from '../utils/documentActivityTiming';
import DiligenceDocumentRunCard from './DiligenceDocumentRunCard';
import { restoreFocusWithoutMobileComposer } from '../utils/composerFocus';
import {
  applyNativeSourceCitations,
  getSourceLabelForUrl,
  isMarkdownSourceLink,
  normalizeCitationMarkdownForRender
} from '../utils/citationFormatting';

interface MessageBubbleProps {
  message: Message;
  onReportClick?: () => void;
  onUpgradeClick?: () => void;
  shouldAnimateAppearance?: boolean;
  onStreamComplete?: () => void;
  onStreamProgress?: () => void;
  onRetryClick?: () => void;
  isRetrying?: boolean;
  onArtifactDownload?: (artifactId: string, fileName: string) => Promise<void>;
  turnStartedAtMs?: number;
  turnRun?: DiligenceDocumentRun;
  onImageDownload?: (index: number) => Promise<void>;
  onDocumentDownload?: () => Promise<void>;
  onOpenChat?: (id: string) => void;
  mentionableChats?: MentionableChat[];
}

const simplifyUrlForClipboard = (url: string) => {
  try {
    const parsedUrl = new URL(url);
    Array.from(parsedUrl.searchParams.keys()).forEach((key) => {
      if (key.toLowerCase().startsWith('utm_')) {
        parsedUrl.searchParams.delete(key);
      }
    });
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const stripInlineMarkdown = (text: string) => text
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/\*([^*]+)\*/g, '$1')
  .replace(/__([^_]+)__/g, '$1')
  .replace(/_([^_]+)_/g, '$1')
  .replace(/`([^`]+)`/g, '$1')
  .trim();

const formatBotTextForClipboard = (text: string, sourceCitations: Message['sourceCitations'] = []) => {
  return normalizeCitationMarkdownForRender(applyNativeSourceCitations(text, sourceCitations))
    .replace(/(?:cite)?turn\d+search\d+/g, '')
    .split(/^#{1,6}\s*fuentes\s+consultadas\s*$/im)[0]
    .replace(/\(?\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)\)?/g, (_match, label: string, url: string) => {
      const cleanLabel = stripInlineMarkdown(label);
      const sourceLabel = getSourceLabelForUrl(url, cleanLabel, sourceCitations);
      const cleanUrl = simplifyUrlForClipboard(url);
      return `${cleanLabel || sourceLabel} (${sourceLabel}): ${cleanUrl}`;
    })
    .replace(/\[(?:Doctrina operativa|Base doctrina)\]/gi, '(Doctrina operativa)')
    .split('\n')
    .map((line) => line.replace(/^#{1,6}\s*/, ''))
    .join('\n')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const cleanTechnicalArtifacts = (text: string) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/(?:cite)?turn\d+search\d+/g, '');
};

const splitTextTokens = (text: string) => text.match(/\s+|\S+/g) ?? [];

const SOURCES_HEADING_REGEX = /^#{1,6}\s*fuentes\s+consultadas\s*$/i;

const removeConsultedSourcesSection = (text: string) => {
  const lines = text.split('\n');
  const sourceHeadingIndex = lines.findIndex((line) => SOURCES_HEADING_REGEX.test(line.trim()));

  if (sourceHeadingIndex === -1) {
    return text;
  }

  return lines.slice(0, sourceHeadingIndex).join('\n').trimEnd();
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onReportClick,
  onUpgradeClick,
  shouldAnimateAppearance = false,
  onStreamComplete,
  onStreamProgress,
  onRetryClick,
  isRetrying = false,
  onArtifactDownload,
  turnStartedAtMs,
  turnRun,
  onImageDownload,
  onDocumentDownload,
  onOpenChat,
  mentionableChats = []
}) => {
  const isUser = message.sender === 'user';
  const activityStartedAtMs = getActivityStartTime(turnRun, turnStartedAtMs);
  const hasArtifacts = getMessageArtifacts(message).length > 0;
  const messageImages = message.imageUrls?.length ? message.imageUrls : message.imageUrl ? [message.imageUrl] : [];
  const isOptimisticImage = Boolean(messageImages.some((url) => url.startsWith('blob:')) || message.isUploading);
  const suppressDocumentStartAcknowledgement = Boolean(
    !isUser
    && (message.documentStartAcknowledgement === true || (
      message.diligenceStatus === 'ready_to_render'
      && !message.documentRun
      && !hasArtifacts
      && message.text?.trim() === 'La generación y la revisión del documento han comenzado.'
    ))
  );
  const suppressRunningDocumentText = !isUser && message.documentRun?.status === 'running';
  const visibleMessageText = suppressDocumentStartAcknowledgement || suppressRunningDocumentText
    ? '' : typeof message.text === 'string' ? message.text : '';
  const hasText = visibleMessageText.trim().length > 0;
  const finishedAtMs = Date.parse(message.timestamp);
  const showTurnDuration = !isUser && !message.documentRun
    && !suppressDocumentStartAcknowledgement && Number.isFinite(activityStartedAtMs)
    && Number.isFinite(finishedAtMs) && finishedAtMs >= Number(activityStartedAtMs);
  const hasAttachment = Boolean(messageImages.length || message.audioUrl || message.attachmentIds?.length || message.attachmentName || hasArtifacts || (!isUser && message.documentRun));
  const hasDocumentAttachment = Boolean(message.attachmentName || (message.attachmentIds?.length ?? 0) > 0);
  const isFailed = message.generationStatus === 'failed';
  const isPendingUpload = Boolean(message.isUploading && !isFailed);
  const onStreamCompleteRef = useRef(onStreamComplete);
  const onStreamProgressRef = useRef(onStreamProgress);

  const [animatedResponseKey, setAnimatedResponseKey] = useState<string | null>(null);
  const [visibleAnimatedWordCount, setVisibleAnimatedWordCount] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');

  useEffect(() => {
    onStreamCompleteRef.current = onStreamComplete;
  }, [onStreamComplete]);

  useEffect(() => {
    onStreamProgressRef.current = onStreamProgress;
  }, [onStreamProgress]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);

    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  const shouldAnimate = useMemo(() => {
    if (isUser) return false;
    return shouldAnimateAppearance;
  }, [isUser, shouldAnimateAppearance]);

  const animationKey = `${message.id}:${visibleMessageText}`;

  const animationWordCount = useMemo(() => {
    const fullText = visibleMessageText;
    return splitTextTokens(cleanTechnicalArtifacts(fullText)).filter((token) => !/^\s+$/.test(token)).length;
  }, [visibleMessageText]);

  const wordAnimationDelayMs = useMemo(() => {
    if (animationWordCount <= 0) return 0;
    if (animationWordCount <= 18) return 50;
    return Math.max(8, Math.floor(900 / animationWordCount));
  }, [animationWordCount]);

  useEffect(() => {
    const fullText = visibleMessageText;

    if (!shouldAnimate || prefersReducedMotion || fullText.length === 0) {
      setAnimatedResponseKey(null);
      setVisibleAnimatedWordCount(0);
      if (shouldAnimate && (prefersReducedMotion || fullText.length === 0)) {
        window.requestAnimationFrame(() => onStreamCompleteRef.current?.());
      }
      return;
    }

    setAnimatedResponseKey(animationKey);
    setVisibleAnimatedWordCount(Math.min(1, animationWordCount));
    window.requestAnimationFrame(() => onStreamProgressRef.current?.());
    const enterDurationMs = 504;
    const totalDurationMs = enterDurationMs + Math.max(0, animationWordCount - 1) * wordAnimationDelayMs;
    const intervalMs = Math.max(8, wordAnimationDelayMs);

    const intervalId = window.setInterval(() => {
      setVisibleAnimatedWordCount((current) => {
        const next = Math.min(animationWordCount, current + 1);
        if (next !== current) {
          window.requestAnimationFrame(() => onStreamProgressRef.current?.());
        }
        if (next >= animationWordCount) {
          window.clearInterval(intervalId);
        }
        return next;
      });
    }, intervalMs);

    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      setVisibleAnimatedWordCount(animationWordCount);
      onStreamCompleteRef.current?.();
    }, totalDurationMs + 80);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [animationKey, animationWordCount, prefersReducedMotion, shouldAnimate, visibleMessageText, wordAnimationDelayMs]);

  const shouldLimitAnimatedWords = shouldAnimate && !prefersReducedMotion;
  const shouldAnimateResponseWords = shouldLimitAnimatedWords;

  const rawCopyText = visibleMessageText;
  const mentionProjection = isUser
    ? refreshChatMentionTitles(visibleMessageText, message.chatMentions ?? [], mentionableChats) : undefined;
  const copyableText = isUser ? mentionProjection!.text : formatBotTextForClipboard(rawCopyText, message.sourceCitations);
  const canCopy = copyableText.trim().length > 0;

  useEffect(() => {
    setCopyState('idle');
  }, [copyableText]);

  const statusText = useMemo(() => {
    if (isPendingUpload) {
      return null;
    }

    if (isFailed) {
      if (message.failureKind === 'unsupported_file') {
        return 'Este tipo de archivo no esta admitido.';
      }

      if (message.failureKind === 'connection') {
        return hasText || hasAttachment
          ? 'No se pudo completar el envio por la conexion.'
          : 'No se pudo enviar este mensaje por la conexion.';
      }

      if (message.failureKind === 'document_upload') {
        return 'No se pudo preparar el documento adjunto. Reintenta el envío.';
      }

      if (message.failureKind === 'generation') {
        return 'Juris no pudo completar esta respuesta. Reintenta el envío.';
      }

      return hasText || hasAttachment
        ? 'No se pudo completar el envio.'
        : 'No se pudo enviar este mensaje.';
    }

    return null;
  }, [hasAttachment, hasText, isFailed, isPendingUpload, message.failureKind]);

  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const readClipboardText = async () => {
    try {
      if (!navigator.clipboard?.readText) return null;
      return await navigator.clipboard.readText();
    } catch {
      return null;
    }
  };

  const copyTextWithModernApi = async (text: string) => {
    if (!navigator.clipboard?.writeText) return false;

    await navigator.clipboard.writeText(text);
    const clipboardText = await readClipboardText();
    return clipboardText === null || clipboardText === '' || clipboardText === text;
  };

  const copyTextWithClipboardItem = async (text: string) => {
    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') return false;

    const item = new ClipboardItem({
      'text/plain': new Blob([text], { type: 'text/plain' })
    });

    await navigator.clipboard.write([item]);
    const clipboardText = await readClipboardText();
    return clipboardText === null || clipboardText === '' || clipboardText === text;
  };

  const copyTextWithTextarea = (text: string) => {
    const textarea = document.createElement('textarea');
    const previousActiveElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '-9999px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    try {
      return document.execCommand('copy');
    } finally {
      document.body.removeChild(textarea);
      restoreFocusWithoutMobileComposer(previousActiveElement);
      window.getSelection()?.removeAllRanges();
    }
  };

  const copyText = async (text: string) => {
    if (copyTextWithTextarea(text)) return true;

    try {
      if (await copyTextWithModernApi(text)) return true;
    } catch {
      // Try the richer Clipboard API before reporting failure.
    }

    try {
      return copyTextWithClipboardItem(text);
    } catch {
      return false;
    }
  };

  const handleCopyMessage = async () => {
    if (!canCopy) return;

    try {
      const copied = await copyText(copyableText);

      if (copied) {
        setCopyState('copied');
        window.setTimeout(() => setCopyState('idle'), 1800);
        return;
      }

      throw new Error('copy_command_failed');
    } catch (error) {
      console.warn('No se pudo copiar el mensaje.', error);
      setCopyState('failed');
      window.setTimeout(() => setCopyState('idle'), 2200);
    }
  };

  const isValidUrl = (url: string) => {
    try {
      const trimmed = url.trim();
      return trimmed.startsWith('http://') || trimmed.startsWith('https://');
    } catch {
      return false;
    }
  };

  const getSourceDisplay = (url: string, label: string) => {
    return getSourceLabelForUrl(url, label, message.sourceCitations);
  };

  const renderAnimatedTextTokens = (
    text: string,
    keyPrefix: string,
    wordCounter: { current: number },
    visibleWordLimit: number | null
  ): React.ReactNode[] => {
    return splitTextTokens(text).map((token, index) => {
      if (/^\s+$/.test(token)) {
        if (visibleWordLimit !== null && wordCounter.current >= visibleWordLimit) return null;
        return <span key={`${keyPrefix}-space-${index}`} className="whitespace-pre-wrap">{token}</span>;
      }

      const delay = visibleWordLimit === null ? wordCounter.current * wordAnimationDelayMs : 0;
      const shouldRenderWord = visibleWordLimit === null || wordCounter.current < visibleWordLimit;
      wordCounter.current += 1;

      if (!shouldRenderWord) return null;

      return (
        <span
          key={`${keyPrefix}-word-${index}`}
          className="response-word-crossfade-unit inline-block whitespace-pre-wrap"
          style={{ animationDelay: `${delay}ms` }}
        >
          {token}
        </span>
      );
    });
  };

  const parseMarkdown = (
    text: string,
    animateWords = false,
    wordCounter: { current: number } = { current: 0 },
    keyPrefix = 'markdown',
    visibleWordLimit: number | null = null
  ): React.ReactNode[] => {
    if (!text || typeof text !== 'string') return [text as any];

    const renderSourceLink = (label: string, url: string, key: React.Key) => {
      const href = url.trim();

      if (!isValidUrl(href)) {
        return null;
      }

      const displayLabel = getSourceDisplay(href, label);
      const shouldRenderLink = !animateWords || visibleWordLimit === null || wordCounter.current < visibleWordLimit;
      const animationProps = animateWords
        ? {
            style: { animationDelay: `${visibleWordLimit === null ? wordCounter.current * wordAnimationDelayMs : 0}ms` },
            'aria-label': displayLabel
          }
        : {};

      if (animateWords) {
        wordCounter.current += 1;
      }

      if (!shouldRenderLink) return null;

      return (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`mx-1 inline-flex h-4 items-center rounded-full border border-line bg-juris-soft px-1.5 text-[9px] font-semibold leading-none text-juris no-underline align-[0.12em] transition-colors hover:border-line hover:bg-juris-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-1 ${animateWords ? 'response-word-crossfade-unit' : ''}`}
          title={label}
          {...animationProps}
        >
          {displayLabel}
        </a>
      );
    };

    const renderPlainTextPart = (part: string, index: number) => {
      const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
      const nodes: React.ReactNode[] = [];
      let lastIndex = 0;

      for (const match of part.matchAll(linkRegex)) {
        const matchIndex = match.index ?? 0;
        const [rawLink, label, url] = match;

        if (matchIndex > lastIndex) {
          const textSegment = part.slice(lastIndex, matchIndex).replace(/\s*\(\s*$/, ' ');
          nodes.push(animateWords ? (
            <React.Fragment key={`${keyPrefix}-text-${index}-${nodes.length}`}>
              {renderAnimatedTextTokens(textSegment, `${keyPrefix}-text-${index}-${nodes.length}`, wordCounter, visibleWordLimit)}
            </React.Fragment>
          ) : (
            <span key={`${keyPrefix}-text-${index}-${nodes.length}`}>{textSegment}</span>
          ));
        }

        nodes.push(
          renderSourceLink(label, url, `${keyPrefix}-link-${index}-${nodes.length}`)
          ?? <span key={`${keyPrefix}-raw-link-${index}-${nodes.length}`}>{rawLink}</span>
        );
        lastIndex = matchIndex + rawLink.length;
      }

      if (lastIndex < part.length) {
        const textSegment = nodes.length > 0
          ? part.slice(lastIndex).replace(/^\s*\)\s*/, '')
          : part.slice(lastIndex);
        nodes.push(animateWords ? (
          <React.Fragment key={`${keyPrefix}-text-${index}-${nodes.length}`}>
            {renderAnimatedTextTokens(textSegment, `${keyPrefix}-text-${index}-${nodes.length}`, wordCounter, visibleWordLimit)}
          </React.Fragment>
        ) : (
          <span key={`${keyPrefix}-text-${index}-${nodes.length}`}>{textSegment}</span>
        ));
      }

      if (nodes.length > 0) {
        return <React.Fragment key={index}>{nodes}</React.Fragment>;
      }

      if (animateWords) {
        return (
          <React.Fragment key={index}>
            {renderAnimatedTextTokens(part, `${keyPrefix}-text-${index}`, wordCounter, visibleWordLimit)}
          </React.Fragment>
        );
      }

      return <span key={index}>{part}</span>;
    };

    const tokenRegex = /\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\(https?:\/\/[^)\s]+\)|\[Doctrina operativa\]|\[Base doctrina\]/g;
    const parts: Array<{ text: string; token: boolean }> = [];
    let lastIndex = 0;
    const isCitationToken = (value: string) => (
      isMarkdownSourceLink(value)
      || /^\[(?:Doctrina operativa|Base doctrina)\]$/i.test(value.trim())
    );

    for (const match of text.matchAll(tokenRegex)) {
      const index = match.index ?? 0;
      if (index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, index), token: false });
      }
      parts.push({ text: match[0], token: true });
      lastIndex = index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), token: false });
    }

    const normalizedParts = parts.map((part, index) => {
      if (part.token) return part;

      const previousPart = parts[index - 1];
      const nextPart = parts[index + 1];
      let normalizedText = part.text;

      if (nextPart?.token && isCitationToken(nextPart.text)) {
        normalizedText = normalizedText.replace(/\s*\(\s*$/, ' ');
      }

      if (previousPart?.token && isCitationToken(previousPart.text)) {
        normalizedText = normalizedText.replace(/^\s*\)\s*/, '');
      }

      return { ...part, text: normalizedText };
    });
    const punctuationNormalizedParts = normalizedParts.map((part, index, currentParts) => {
      if (part.token || !part.text) return part;

      const nextPart = currentParts[index + 1];
      const nextNextPart = currentParts[index + 2];
      if (!nextPart?.token || !isCitationToken(nextPart.text) || nextNextPart?.token) return part;

      const punctuationMatch = nextNextPart?.text.match(/^([.,;:])(\s*)/);
      if (!punctuationMatch) return part;

      nextNextPart.text = nextNextPart.text.slice(punctuationMatch[0].length);
      return { ...part, text: `${part.text.trimEnd()}${punctuationMatch[1]}${punctuationMatch[2] || ' '}` };
    });

    return punctuationNormalizedParts.map(({ text: part, token }, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const content = part.slice(2, -2);
        return (
          <strong key={index} className="font-bold">
            {animateWords ? renderAnimatedTextTokens(content, `${keyPrefix}-strong-${index}`, wordCounter, visibleWordLimit) : content}
          </strong>
        );
      }

      if (part.startsWith('*') && part.endsWith('*')) {
        const content = part.slice(1, -1);
        return (
          <em key={index} className="italic">
            {animateWords ? renderAnimatedTextTokens(content, `${keyPrefix}-em-${index}`, wordCounter, visibleWordLimit) : content}
          </em>
        );
      }

      if (token && part.includes('[') && part.includes(']')) {
        const normalized = part.trim().replace(/^\(\s*/, '').replace(/\s*\)$/, '');
        const normalizedLower = normalized.toLowerCase();

        if (normalizedLower === '[doctrina operativa]' || normalizedLower === '[base doctrina]') {
          const shouldRenderDoctrine = !animateWords || visibleWordLimit === null || wordCounter.current < visibleWordLimit;
          const animationProps = animateWords
            ? {
                style: { animationDelay: `${visibleWordLimit === null ? wordCounter.current * wordAnimationDelayMs : 0}ms` },
                'aria-label': 'Doctrina operativa'
              }
            : {};

          if (animateWords) {
            wordCounter.current += 1;
          }

          if (!shouldRenderDoctrine) return null;

          return (
            <span
              key={index}
              className={`inline-flex h-4 items-center rounded-full border border-warning bg-warning-soft px-1.5 text-[9px] font-semibold leading-none text-warning align-[0.12em] ${animateWords ? 'response-word-crossfade-unit' : ''}`}
              title="Doctrina operativa"
              {...animationProps}
            >
              Doctrina operativa
            </span>
          );
        }

        const linkMatch = normalized.match(/^\[(.*?)\]\s*\((.*?)\)$/);

        if (linkMatch) {
          const [, label, url] = linkMatch;
          const sourceLink = renderSourceLink(label, url, index);
          if (sourceLink) return sourceLink;
        }
      }

      return renderPlainTextPart(part, index);
    });
  };

  const renderFormattedText = (rawText: any) => {
    if (typeof rawText !== 'string') {
      if (!rawText) return null;
      return <span>{String(rawText)}</span>;
    }

    const cleanedText = normalizeCitationMarkdownForRender(
      removeConsultedSourcesSection(cleanTechnicalArtifacts(
        applyNativeSourceCitations(rawText, message.sourceCitations)
      ))
    );
    const lines = cleanedText.split('\n');
    const wordCounter = { current: 0 };
    const visibleWordLimit = shouldLimitAnimatedWords
      ? (animatedResponseKey === animationKey ? visibleAnimatedWordCount : 0)
      : null;
    const renderedLines: React.ReactNode[] = [];

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      const wordCountBeforeLine = wordCounter.current;

      if (visibleWordLimit !== null && wordCountBeforeLine >= visibleWordLimit && trimmed !== '') {
        return;
      }

      if (trimmed.startsWith('#')) {
        const content = trimmed.replace(/^#+\s*/, '');
        const parsedContent = parseMarkdown(content, shouldAnimateResponseWords, wordCounter, `heading-${index}`, visibleWordLimit);
        if (visibleWordLimit !== null && wordCounter.current === wordCountBeforeLine) return;

        renderedLines.push(
          <div key={index} className="text-lg font-bold leading-tight mb-2 mt-3 first:mt-0 text-ink">
            {parsedContent}
          </div>
        );
        return;
      }

      if (trimmed === '') {
        if (visibleWordLimit !== null && wordCounter.current >= visibleWordLimit) return;
        renderedLines.push(<div key={index} className="h-3" />);
        return;
      }

      const parsedLine = parseMarkdown(line, shouldAnimateResponseWords, wordCounter, `line-${index}`, visibleWordLimit);
      if (visibleWordLimit !== null && wordCounter.current === wordCountBeforeLine) return;

      renderedLines.push(
        <div key={index} className="leading-relaxed break-words min-h-[1.2em]">
          {parsedLine}
        </div>
      );
    });

    return renderedLines;
  };

  const metadata = (
    <div className={`mt-1.5 flex items-center gap-1.5 text-[10px] leading-none ${isUser ? 'justify-end text-ink/55' : 'justify-start text-muted'}`}>
      <span>{formatTime(message.timestamp)}</span>
      {canCopy && (
        <>
          <button
            type="button"
            onClick={handleCopyMessage}
            className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-all active:scale-95 focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2 ${copyState === 'failed' ? 'text-danger hover:bg-danger-soft' : isUser ? 'text-ink/60 hover:bg-action-hover/10 hover:text-ink' : 'text-muted hover:bg-juris-soft hover:text-juris'}`}
            aria-label={copyState === 'failed' ? 'No se pudo copiar el mensaje' : 'Copiar mensaje'}
            title={copyState === 'failed' ? 'No se pudo copiar' : 'Copiar mensaje'}
          >
            {copyState === 'copied' ? <Check size={14} /> : copyState === 'failed' ? <AlertCircle size={14} /> : <Copy size={14} />}
          </button>
          <span className="sr-only" aria-live="polite">
            {copyState === 'copied' ? 'Mensaje copiado' : copyState === 'failed' ? 'No se pudo copiar el mensaje' : ''}
          </span>
        </>
      )}
    </div>
  );

  const content = (
    <>
      <ChatImageGallery images={messageImages} isOptimistic={isOptimisticImage} onDownload={onImageDownload} />

      {message.audioUrl && (
        <ChatAudioAttachment key={message.id} message={{ ...message, audioUrl: message.audioUrl }} isUser={isUser} />
      )}

      {hasDocumentAttachment && (
        <ChatDocumentAttachment name={message.attachmentName || 'Documento adjunto'} isUser={isUser} isUploading={isPendingUpload} onDownload={onDocumentDownload} />
      )}

      {hasArtifacts && !message.documentRun && (
        <DiligenceArtifactList message={message} onDownload={onArtifactDownload} />
      )}

      {hasText && (isUser || !message.documentRun) && (
        <div className="text-[15px] md:text-base">
          {shouldLimitAnimatedWords && <span className="sr-only">{cleanTechnicalArtifacts(visibleMessageText)}</span>}
          <div aria-hidden={shouldLimitAnimatedWords ? true : undefined}>
            {isUser && message.chatMentions?.length
              ? <div className="whitespace-pre-wrap break-words"><ChatMentionText text={mentionProjection!.text} mentions={mentionProjection!.mentions} onOpenChat={onOpenChat} /></div>
              : renderFormattedText(visibleMessageText)}
          </div>
        </div>
      )}

      {statusText && (
        <div className={`mt-2 flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-xs leading-snug ${isUser ? 'bg-panel/10 text-juris' : 'bg-juris-soft text-ink'}`}>
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {isFailed ? (
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            ) : (
              <Loader2 size={14} className="mt-0.5 flex-shrink-0 animate-spin" />
            )}
            <span>{statusText}</span>
          </div>
          {isFailed && onRetryClick && (
            <button
              type="button"
              onClick={onRetryClick}
              disabled={isRetrying}
              className={`inline-flex min-h-8 items-center gap-1 rounded-full px-2.5 py-1 font-bold transition-all active:scale-95 disabled:opacity-70 ${isUser ? 'bg-panel text-ink hover:bg-juris-soft' : 'bg-action text-white hover:bg-action-hover'}`}
              aria-label="Reintentar envio"
            >
              {isRetrying ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RotateCcw size={13} />
              )}
              <span>{isRetrying ? 'Reintentando' : 'Reintentar'}</span>
            </button>
          )}
        </div>
      )}

      {message.action === 'report' && (
        <div className="mt-3">
          <button
            onClick={onReportClick}
            className="flex items-center gap-2 px-4 py-2 bg-action text-white rounded-xl hover:bg-action-hover transition-all font-bold shadow-sm active:scale-95"
          >
            <Flag size={18} />
            <span>Reportar</span>
          </button>
        </div>
      )}

      {message.action === 'upgrade' && (
        <div className="mt-3 relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-warning-soft to-warning-soft rounded-xl blur opacity-20 transition duration-500"></div>
          <button
            onClick={onUpgradeClick}
            className="relative flex items-center gap-2 px-4 py-2 bg-panel border-2 border-warning text-warning rounded-xl hover:bg-warning-soft transition-all font-bold shadow-sm active:scale-95"
          >
            <Sparkles size={18} className="text-warning" />
            <span>Mejorar Plan</span>
          </button>
        </div>
      )}
    </>
  );

  if (suppressDocumentStartAcknowledgement) return null;

  if (!isUser) {
    if (message.documentRun) {
      const terminal = message.documentRun.status !== 'running';
      return (
        <div
          id={`msg-${message.id}`}
          className="flex w-full justify-start animate-message-appear"
        >
          <div className="w-full min-w-0">
            <DiligenceDocumentRunCard run={message.documentRun} precedingRun={turnRun} turnStartedAtMs={activityStartedAtMs} />
            {terminal && hasText && (
              <div key={`${message.id}-${message.documentRun.status}`} className="animate-message-appear px-1 text-[15px] text-ink md:text-base">
                {renderFormattedText(visibleMessageText)}
              </div>
            )}
            {terminal && hasArtifacts && (
              <div className="mt-3 max-w-full sm:max-w-[75%]">
                <DiligenceArtifactList message={message} onDownload={onArtifactDownload} />
              </div>
            )}
            {terminal && metadata}
          </div>
        </div>
      );
    }

    const needsFramedBotContent = Boolean(message.imageUrl || message.audioUrl || message.attachmentIds?.length || message.attachmentName || hasArtifacts || statusText || message.action);

    return (
      <div
        id={`msg-${message.id}`}
        className="flex w-full mb-5 justify-start animate-message-appear"
      >
        <div className="w-full min-w-0">
          {showTurnDuration && (
            <DiligenceDocumentRunCard turnStartedAtMs={activityStartedAtMs} run={{
              ...(turnRun ?? { version: 1, stage: 'received', events: [], timingComplete: true }),
              status: 'completed', startedAtMs: activityStartedAtMs, finishedAtMs
            }} />
          )}
          <div className={`${hasArtifacts ? 'w-full min-w-0 sm:w-fit sm:max-w-[75%]' : needsFramedBotContent ? 'max-w-[85%] sm:max-w-[75%] rounded-lg border border-line/70 bg-panel/70 px-3 py-2 shadow-sm' : 'w-full px-1 py-1 text-ink'} text-sm md:text-base`}>
            {content}
          </div>
          {metadata}
        </div>
      </div>
    );
  }

  return (
    <div
      id={`msg-${message.id}`}
      className="flex w-full mb-5 justify-end"
    >
      <div className={`${message.audioUrl ? 'w-[270px] max-w-full' : 'max-w-[85%] sm:max-w-[75%]'}`}>
        <div className={`relative px-3 ${message.audioUrl ? 'pt-2 pb-1 rounded-2xl' : 'py-2 rounded-lg rounded-tr-none'} shadow-sm text-sm md:text-base bg-action text-white`}>
          {content}
        </div>
        {metadata}
      </div>
    </div>
  );
};

export default MessageBubble;
